'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './AiSchemaContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

const steps = [
  {
    number: '1',
    title: 'Paste your page URL',
    description:
      'Drop in the full address of the page you want to mark up, a blog post, a product page, a recipe, an event listing, anything. No account, no sign-up, completely free. One URL is all the tool needs to start.',
  },
  {
    number: '2',
    title: 'The AI reads the page and detects what it is',
    description:
      'The tool crawls the page, reads the visible content, and works out what kind of page it is. It then picks the right schema.org type and drafts valid JSON-LD structured data from what it found, filling in the headline, dates, prices, or other details it can see.',
  },
  {
    number: '3',
    title: 'Review, copy, and validate',
    description:
      'This step matters most. Read the generated markup and check every value against your actual page before you use it. The AI drafts from what it can read, so confirm it is accurate, copy the JSON-LD into your page head, then run it through the Google Rich Results Test.',
  },
];

const detectableTypes = [
  { type: 'Article / BlogPosting', role: 'Marks up written content so search engines read the headline, author, and dates as data.', when: 'A blog post, guide, or news story.', badge: 'Content', color: '#166534', bg: '#dcfce7' },
  { type: 'Product', role: 'Describes an item for sale, including its name, description, and offer details.', when: 'An online store or single product page.', badge: 'Commerce', color: '#1e40af', bg: '#dbeafe' },
  { type: 'Recipe', role: 'Captures ingredients, steps, and timing so a dish can qualify for recipe rich results.', when: 'A cooking or food page.', badge: 'Food', color: '#92400e', bg: '#fef3c7' },
  { type: 'Event', role: 'Describes a happening with a name, date, and location.', when: 'A concert, webinar, or listing page.', badge: 'Event', color: '#5b21b6', bg: '#ede9fe' },
  { type: 'VideoObject', role: 'Describes an embedded video with its title, thumbnail, and upload date.', when: 'A page built around a video.', badge: 'Media', color: '#9d174d', bg: '#fce7f3' },
  { type: 'JobPosting', role: 'Marks up an open role with its title, location, and hiring organization.', when: 'A careers or job listing page.', badge: 'Jobs', color: '#0e7490', bg: '#cffafe' },
  { type: 'LocalBusiness / Organization', role: 'Describes a company or physical business, its name, contact details, and identity.', when: 'A homepage, about page, or storefront.', badge: 'Business', color: '#3730a3', bg: '#e0e7ff' },
  { type: 'BreadcrumbList', role: 'Describes the navigation trail so search engines show a tidy path in results.', when: 'Any page inside a clear site hierarchy.', badge: 'Structure', color: '#374151', bg: '#e5e7eb' },
];

const unlocks = [
  { icon: '⭐', title: 'Rich-result eligibility', description: 'Valid structured data makes your page eligible for the enhanced listings Google shows for articles, products, recipes, events, and more. Eligibility is never guaranteed, but the markup is the entry ticket.' },
  { icon: '⏱️', title: 'Saves time versus hand-coding', description: 'Writing JSON-LD by hand for the right schema.org type takes research and care. Auto-detection gives you a working first draft in seconds, so you spend your time reviewing instead of typing braces.' },
  { icon: '🙌', title: 'Friendly to non-technical users', description: 'You do not need to know the schema.org vocabulary or JSON syntax to get started. Paste a URL and the tool handles the structure, so marketers and site owners can add schema without a developer.' },
  { icon: '🧩', title: 'Covers many page types', description: 'One tool detects and drafts markup across articles, products, recipes, events, videos, jobs, and business pages, so you are not hunting for a separate generator for every kind of page.' },
  { icon: '✏️', title: 'A starting point you refine', description: 'Think of the output as a solid draft, not a finished product. It gets the structure and the obvious fields right, then you correct and complete it so it matches your page exactly.' },
  { icon: '🤖', title: 'Clarity for AI answers', description: 'Structured data makes your content easier for search engines and AI answer engines to understand and cite. Clear data means your page is easier to represent accurately when machines summarize the web.' },
];

const cautions = [
  { icon: '🔍', title: 'Always review before publishing', description: 'The single most important habit. AI drafts the markup from what it reads, and it can guess wrong. Read every field and confirm it is true before you add the schema to a live page.', takeaway: 'Never ship it unread' },
  { icon: '🚫', title: 'Never mark up facts that are not on the page', description: 'Schema must describe content that is actually visible to visitors. If a rating, price, or detail is not on the page, do not leave it in the markup just because the AI added it.', takeaway: 'Match the visible page' },
  { icon: '🧱', title: 'JS-rendered content can be missed', description: 'If key details load only through JavaScript, a crawl may not see them. The AI can miss or misread content that is not in the initial HTML, so check anything that renders client-side.', takeaway: 'Watch client-side content' },
  { icon: '💲', title: 'Verify prices, ratings, and dates yourself', description: 'These are the values most likely to be wrong or out of date in an auto-generated draft. Confirm every number and date against the real page before you rely on the markup.', takeaway: 'Double-check the numbers' },
  { icon: '✅', title: 'Validate with Google before you trust it', description: 'Run the output through the Google Rich Results Test and the Schema.org Validator. A single syntax slip can invalidate the whole block, and validation catches it before Google does.', takeaway: 'Validate every time' },
  { icon: '👁️', title: 'Do not mark up invisible content', description: 'Google guidelines require structured data to reflect what users can see. Marking up hidden or off-page content is a guideline violation that can hurt you, so keep the schema to visible content only.', takeaway: 'Visible content only' },
];

const tips = [
  { number: '01', title: 'Prefer the manual tool for full control', description: 'When you want to type every field yourself and know exactly what goes in, a manual generator gives you precise control over Article markup. Use the AI tool to draft fast, the manual tool to fine-tune.', cta: 'Try the manual Article Schema Generator', ctaHref: '/free-tools/seo/article-schema-generator' },
  { number: '02', title: 'Pair it with site-wide schema', description: 'Auto-detected markup describes one page. Your Organization and WebSite schema describes the whole brand. Use both, and let our free Website Schema Generator build the site-wide part.', cta: 'Try the Website Schema Generator', ctaHref: '/free-tools/seo/website-schema-generator' },
  { number: '03', title: 'Re-run it after the page changes', description: 'The markup is a snapshot of the page as it was crawled. When you update the content, prices, or dates, run the URL through again so the schema keeps matching what visitors see.', cta: null, ctaHref: null },
  { number: '04', title: 'Keep your content crawlable', description: 'The tool can only mark up what it can read. If the important details load only through JavaScript, make sure they are in the HTML too, so both the AI and search engines can see them.', cta: null, ctaHref: null },
  { number: '05', title: 'Combine detected types when it fits', description: 'A single page can carry more than one type, like an Article plus a BreadcrumbList. Review the draft and keep the types that genuinely describe the page, removing any that do not apply.', cta: null, ctaHref: null },
  { number: '06', title: 'Validate after every change', description: 'Re-run the Google Rich Results Test whenever you edit the markup or redesign the page. It is the fastest way to catch an error before it reaches Google.', cta: null, ctaHref: null },
];

const glossary = [
  { term: 'JSON-LD', definition: 'The recommended format for structured data: a small block of JSON placed in the head of a page. It lives separately from your visible content, so it is easy to add without touching your design.', example: 'A <script> block', when: 'Every page you mark up' },
  { term: 'structured data', definition: 'Information about a page written in a shared vocabulary that machines can read, so search engines understand what the page is rather than guessing from the text.', example: 'schema.org markup', when: 'Helping machines read your page' },
  { term: 'rich result', definition: 'An enhanced search listing, such as a recipe card, star rating, or event date, that a page becomes eligible for with valid structured data. Eligibility is never guaranteed.', example: 'A recipe card', when: 'Standing out in search' },
  { term: 'crawl', definition: 'The process of a tool or search engine fetching a page and reading its content. This tool crawls your URL to work out what the page is and what to mark up.', example: 'Fetching your URL', when: 'Detecting the page type' },
  { term: '@type', definition: 'The schema.org property that names what a page is, such as Article, Product, or Recipe. Choosing the right @type is the core of good structured data.', example: 'Product', when: 'Every schema block' },
  { term: 'canonical', definition: 'The single preferred URL for a piece of content when the same content is reachable from more than one address. Mark up the canonical version to avoid duplicate signals.', example: 'The primary URL', when: 'Pages with duplicate paths' },
];

interface IProps {
  relatedPosts: IRelatedPost[];
}

export const AiSchemaContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free AI schema generator</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up, completely free. Paste one page URL, and the tool crawls
            the page, works out what it is, and drafts the right schema.org JSON-LD for you to review.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>The Basics</StyledSectionLabel>
          <StyledSectionHeading>What is AI schema generation, and why start from a URL?</StyledSectionHeading>
          <StyledSectionIntro>
            Instead of picking a schema type and filling in fields by hand, you hand the tool one
            address and it reads the page to decide what markup fits.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">The idea</p>
            <p className="formula">Your URL → AI reads the page → the right schema.org JSON-LD</p>
            <p className="example">
              Result: <span>a drafted markup block you review, correct, and copy into your page</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              AI schema generation means the tool does the detection for you. You paste a single URL,
              it crawls the page, reads the visible content, and works out what the page is: an
              article, a product, a recipe, an event, and more. It then drafts valid JSON-LD for that
              type from what it found on the page.
            </p>
            <p>
              One-URL auto-detection is useful because it removes the two hardest parts of doing this
              by hand: knowing which schema.org type to use, and writing correct JSON syntax. That
              makes it a fast starting point, especially if you are not a developer and you have many
              pages to cover.
            </p>
            <p>
              Be honest about what it is, though. The AI drafts the markup from what it can read, and
              it can miss things or guess wrong. Treat the output as a first draft, not a finished
              answer. You should verify every value against your real page and validate the block
              before you use it. Clear, accurate data is what makes your content easy for search
              engines and AI assistants to represent correctly.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>What It Can Detect</StyledSectionLabel>
          <StyledSectionHeading>The types this tool can detect and output</StyledSectionHeading>
          <StyledSectionIntro>
            The tool matches your page to a schema.org type. Here are the common ones it can detect,
            what each one does, and the kind of page it usually fits.
          </StyledSectionIntro>
          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>What it is</th>
                  <th>Typical page</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {detectableTypes.map((row) => (
                  <tr key={row.type}>
                    <td className="col-name">{row.type}</td>
                    <td className="col-note">{row.role}</td>
                    <td className="col-note">{row.when}</td>
                    <td><StyledBadge color={row.color} bg={row.bg}>{row.badge}</StyledBadge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>
          <StyledTableFootnote>
            Detection depends on what is visible and crawlable on the page. Always confirm the type
            and every value before you use the markup.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>What It Can Unlock</StyledSectionLabel>
          <StyledSectionHeading>What an AI schema generator can do for you</StyledSectionHeading>
          <StyledSectionIntro>
            Auto-detection saves time and lowers the bar for adding structured data. It helps machines
            understand your content, though eligibility for any search feature is never guaranteed.
          </StyledSectionIntro>
          <StyledFactorsGrid>
            {unlocks.map((f) => (
              <StyledFactorCard key={f.title}>
                <span className="factor-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.description}</p>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Avoid These</StyledSectionLabel>
          <StyledSectionHeading>Six things to watch with AI-generated schema</StyledSectionHeading>
          <StyledSectionIntro>
            Auto-generated markup helps only when it is accurate and honest. The theme across all of
            these is simple: review the output before you trust it.
          </StyledSectionIntro>
          <StyledFactorsGrid>
            {cautions.map((f) => (
              <StyledFactorCard key={f.title}>
                <span className="factor-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.description}</p>
                <span className="factor-stat">{f.takeaway}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Get More From It</StyledSectionLabel>
          <StyledSectionHeading>6 tips for using AI-generated schema well</StyledSectionHeading>
          <StyledSectionIntro>
            Practical ways to get more from auto-detection and to check its work. All CommonNinja
            tools mentioned are free.
          </StyledSectionIntro>
          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p>{tip.description}</p>
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} {'→'}
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Glossary</StyledSectionLabel>
          <StyledSectionHeading>Key structured data terms</StyledSectionHeading>
          <StyledSectionIntro>
            A quick reference for the words behind schema and auto-detection.
          </StyledSectionIntro>
          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Definition</th>
                  <th>Example</th>
                  <th>When It Matters</th>
                </tr>
              </thead>
              <tbody>
                {glossary.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.example}</span></td>
                    <td>{row.when}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {relatedPosts.length > 0 && (
        <StyledSection bg="var(--gray-50)">
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on SEO and structured data</StyledSectionHeading>
            <StyledSectionIntro>
              Go deeper on technical SEO and making your content easier for search engines to understand.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard key={post.slug} href={`/blog/${post.slug}`}>
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article {'→'}</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}
    </StyledContentWrapper>
  );
};
